/**
 * Markdown ファイル群をノートとして取り込む共通ロジック。
 *
 * インポートモーダル（ImportNotesModal）とサイドバーへの OS ファイル D&D
 * （NotesSidebar）の両方から使う。
 *
 * - `.md` / `.markdown` / `.txt` をノート化（ファイル名 = タイトル、中身 = 本文）
 * - 画像は purpose=note で S3 へアップロードし、md 内の相対パス参照
 *   （`![](images/foo.png)` / Obsidian の `![[foo.png]]`）を URL へ置換
 * - フォルダごとドロップされた場合は relativePath のディレクトリ階層を
 *   ノートフォルダとして再現する（フォルダ作成関数が渡された場合のみ。
 *   権限が無いときはドロップ先へフラットに取り込む）
 */
import type { FileWithPath } from "../../../hooks/useS3Upload";
import type {
  CreateNoteParams,
  NoteFolderSummary,
} from "../../../app/services/notes";
import type { InitiateUploadTrigger } from "../../../utils/s3DirectUpload";
import { uploadNoteFile } from "./noteFileUpload";

/** ノート化できる Markdown / テキストファイルか */
export const isMarkdownFileName = (name: string): boolean =>
  /\.(md|markdown|txt)$/i.test(name);

/** ファイル名から拡張子を除いてノートタイトルにする */
export const titleFromFileName = (name: string): string =>
  name.replace(/\.(md|markdown|txt)$/i, "");

/** パスの末尾のファイル名を取り出す */
const basename = (path: string): string => {
  const clean = path.split(/[?#]/)[0];
  return decodeURIComponent(clean.split("/").pop() ?? clean);
};

/**
 * md 本文中の画像参照をアップロード済み URL に置換する。
 * - 標準記法: `![alt](相対パス)` … basename が一致したら URL へ
 * - Obsidian 記法: `![[ファイル名]]` … `![ファイル名](URL)` へ
 */
export const replaceImageRefs = (
  content: string,
  imageUrlByName: Map<string, string>,
): string => {
  let result = content.replace(
    /!\[([^\]]*)\]\(([^)\s]+)\)/g,
    (match, alt: string, path: string) => {
      if (/^https?:\/\//.test(path)) return match;
      const url = imageUrlByName.get(basename(path));
      return url ? `![${alt}](${url})` : match;
    },
  );
  result = result.replace(/!\[\[([^[\]\n]+)\]\]/g, (match, name: string) => {
    const url = imageUrlByName.get(basename(name.trim()));
    return url ? `![${titleFromFileName(name.trim())}](${url})` : match;
  });
  return result;
};

export interface ImportNoteEntriesOptions {
  sectionId: string;
  /** ドロップ先フォルダ（省略 = セクション直下） */
  baseFolderId?: string;
  /** 既存フォルダ一覧（同名フォルダの再利用に使う。createFolder 利用時のみ） */
  existingFolders?: NoteFolderSummary[];
  /** uploadApi の initiateUpload mutation trigger（画像アップロード用） */
  initiateUpload: InitiateUploadTrigger;
  // eslint-disable-next-line no-unused-vars
  createNote: (params: CreateNoteParams) => Promise<unknown>;
  /**
   * フォルダ作成（未指定 = 作成権限なし）。未指定のときはディレクトリ階層を
   * 再現せず、全ノートをドロップ先へフラットに取り込む。
   */
  createFolder?: (
    // eslint-disable-next-line no-unused-vars
    params: { section_id: string; title: string; parent_id?: string },
  ) => Promise<NoteFolderSummary>;
}

export interface ImportNoteEntriesResult {
  noteCount: number;
  folderCount: number;
  /** 受理できなかった（md / 画像以外の）ファイル数 */
  skippedCount: number;
}

/**
 * D&D やファイル選択で受け取ったファイル群をノートとして取り込む。
 * 画像 → フォルダ → ノートの順で処理し、作成した件数を返す。
 * 途中で失敗した場合は例外を投げる（作成済みのものはそのまま残る）。
 */
export const importNoteEntries = async (
  entries: FileWithPath[],
  options: ImportNoteEntriesOptions,
): Promise<ImportNoteEntriesResult> => {
  const mdEntries = entries.filter((e) => isMarkdownFileName(e.file.name));
  const imageEntries = entries.filter(
    (e) =>
      !isMarkdownFileName(e.file.name) && e.file.type.startsWith("image/"),
  );
  const skippedCount = entries.length - mdEntries.length - imageEntries.length;
  if (mdEntries.length === 0) {
    return { noteCount: 0, folderCount: 0, skippedCount };
  }

  // 1. 画像を先にアップロードして basename → URL のマップを作る
  //    （md 内の相対パス参照はディレクトリを問わず basename 一致で置換する）
  const imageUrlByName = new Map<string, string>();
  for (const image of imageEntries) {
    const uploaded = await uploadNoteFile(options.initiateUpload, image.file);
    imageUrlByName.set(image.file.name, uploaded.url);
  }

  // 2. relativePath のディレクトリ階層からフォルダを用意する。
  //    同じ親の下に同名フォルダが既にあれば再利用し、無ければ作成する。
  const folderIdByPath = new Map<string, string | undefined>([
    ["", options.baseFolderId],
  ]);
  const folderKey = (parentId: string | undefined, title: string) =>
    `${parentId ?? ""}\n${title}`;
  const existingByKey = new Map<string, string>(
    (options.existingFolders ?? []).map((f) => [
      folderKey(f.parent_id ?? undefined, f.title),
      f.id,
    ]),
  );
  let folderCount = 0;

  const ensureFolder = async (
    dirPath: string,
  ): Promise<string | undefined> => {
    if (!options.createFolder) return options.baseFolderId;
    if (folderIdByPath.has(dirPath)) return folderIdByPath.get(dirPath);
    const segments = dirPath.split("/");
    const title = segments[segments.length - 1];
    const parentId = await ensureFolder(segments.slice(0, -1).join("/"));
    const existingId = existingByKey.get(folderKey(parentId, title));
    if (existingId) {
      folderIdByPath.set(dirPath, existingId);
      return existingId;
    }
    const created = await options.createFolder({
      section_id: options.sectionId,
      title,
      ...(parentId ? { parent_id: parentId } : {}),
    });
    existingByKey.set(folderKey(parentId, created.title), created.id);
    folderIdByPath.set(dirPath, created.id);
    folderCount += 1;
    return created.id;
  };

  // 3. md ごとに画像参照を置換してノートを作成する
  let noteCount = 0;
  for (const md of mdEntries) {
    const raw = await md.file.text();
    const content = replaceImageRefs(raw, imageUrlByName);
    const dirPath = md.relativePath.split("/").slice(0, -1).join("/");
    const folderId = await ensureFolder(dirPath);
    await options.createNote({
      section_id: options.sectionId,
      ...(folderId ? { folder_id: folderId } : {}),
      title: titleFromFileName(md.file.name),
      content,
    });
    noteCount += 1;
  }

  return { noteCount, folderCount, skippedCount };
};
